// Goal Amount column (amount + frequency + retirement legacy amount + estimate helper).
import { Box, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Typography } from '../../../components/atoms';
import { formatCurrency } from '../../../utils';
import {
    getFrequencyLabel,
    getLegacyAmount,
    getMainAmount,
    GoalInlineRow
} from '../goalInlineHelpers';
import { CurrencyField, EstimateGoalAmountHelper, FrequencySelect, GoalRowCtx, goalsReadTextSx } from './shared';

const AmountLine = ({ label, amount, freq }: { label: string; amount: string; freq: string }) => {
    const theme = useTheme();
    return (
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '12px' }}>
            <Typography
                variant="labels.small"
                label={label}
                sx={{ ...goalsReadTextSx(theme).caption, minWidth: '48px' }}
            />
            <Typography
                variant="body.large"
                label={amount}
                sx={{ ...goalsReadTextSx(theme).value, flex: 1, textAlign: 'right' }}
            />
            <Typography variant="labels.small" label={freq} sx={goalsReadTextSx(theme).caption} />
        </Box>
    );
};

export const GoalAmountCellRead = ({ row }: { row: GoalInlineRow }) => {
    const { t } = useTranslation();
    const legacy = getLegacyAmount(row);
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <AmountLine
                label={row.type === 'wealth' ? t('GOALS_INLINE_COL_GOAL_AMOUNT') : t('GOALS_INLINE_INCOME_LABEL')}
                amount={formatCurrency(getMainAmount(row))}
                freq={getFrequencyLabel(row, t)}
            />
            {row.type === 'retirement' && legacy > 0 ? (
                <AmountLine
                    label={t('GOALS_INLINE_LEGACY_LABEL')}
                    amount={formatCurrency(legacy)}
                    freq={t('GOALS_INLINE_ONE_TIME')}
                />
            ) : null}
        </Box>
    );
};

const AmountRowEdit = ({
    ctx,
    name,
    label,
    right,
    requiredMessage,
    maxDigitsMessage,
    placeholder
}: {
    ctx: GoalRowCtx;
    name: keyof GoalInlineRow & string;
    label: string;
    right: React.ReactNode;
    requiredMessage?: string;
    maxDigitsMessage?: string;
    placeholder?: string;
}) => {
    const theme = useTheme();
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Box sx={{ flex: 1 }}>
                    <CurrencyField
                        ctx={ctx}
                        name={name}
                        required={name !== 'goalAmount' || ctx.row.type !== 'retirement'}
                        requiredMessage={requiredMessage}
                        maxDigitsMessage={maxDigitsMessage}
                        placeholder={placeholder}
                    />
                </Box>
                <Box sx={{ minWidth: '120px' }}>{right}</Box>
            </Box>
            <Typography variant="labels.small" label={label} sx={goalsReadTextSx(theme).caption} />
        </Box>
    );
};

const OneTimeStatic = () => {
    const { t } = useTranslation();
    const theme = useTheme();
    return (
        <Typography
            variant="labels.small"
            label={t('GOALS_INLINE_ONE_TIME')}
            sx={goalsReadTextSx(theme).caption}
        />
    );
};

export const GoalAmountCellEdit = ({ ctx }: { ctx: GoalRowCtx }) => {
    const { t } = useTranslation();
    const type = ctx.row.type;

    if (type === 'income') {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '100%' }}>
                <AmountRowEdit
                    ctx={ctx}
                    name="goalAmount"
                    label={t('GOALS_INLINE_INCOME_LABEL')}
                    placeholder={t('GOALS_INLINE_INCOME_PLACEHOLDER')}
                    right={<FrequencySelect ctx={ctx} required />}
                    requiredMessage={t('INCOME_GOAL_AMOUNT_REQUIRED_ERROR')}
                    maxDigitsMessage={t('INCOME_GOAL_AMOUNT_MAXLENGTH_ERROR')}
                />
                <EstimateGoalAmountHelper />
            </Box>
        );
    }

    if (type === 'wealth') {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '100%' }}>
                <AmountRowEdit
                    ctx={ctx}
                    name="goalAmount"
                    label={t('GOALS_INLINE_COL_GOAL_AMOUNT')}
                    placeholder={t('GOALS_INLINE_AMOUNT_PLACEHOLDER')}
                    right={<OneTimeStatic />}
                    requiredMessage={t('TARGET_GOAL_AMOUNT_REQUIRED_ERROR')}
                    maxDigitsMessage={t('TARGET_GOAL_AMOUNT_MAXLENGTH_ERROR')}
                />
                <EstimateGoalAmountHelper />
            </Box>
        );
    }

    // retirement: withdrawal/income amount + frequency, then legacy amount + one-time
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '100%' }}>
            <AmountRowEdit
                ctx={ctx}
                name="targetedRetirementIncome"
                label={t('GOALS_INLINE_INCOME_LABEL')}
                placeholder={t('GOALS_INLINE_INCOME_PLACEHOLDER')}
                right={<FrequencySelect ctx={ctx} required />}
                requiredMessage={t('INCOME_GOAL_AMOUNT_REQUIRED_ERROR')}
                maxDigitsMessage={t('INCOME_GOAL_AMOUNT_MAXLENGTH_ERROR')}
            />
            <AmountRowEdit
                ctx={ctx}
                name="goalAmount"
                label={t('GOALS_INLINE_LEGACY_LABEL')}
                placeholder={t('GOALS_INLINE_LEGACY_PLACEHOLDER')}
                right={<OneTimeStatic />}
                maxDigitsMessage={t('LEGACY_AMOUNT_MAXLENGTH_ERROR')}
            />
            <EstimateGoalAmountHelper />
        </Box>
    );
};
